import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { 
  Plus, Search, MoreHorizontal, Eye, Edit3, ArrowUpDown, 
  CalendarIcon, FileText, PoundSterling, CheckCircle, Trash2,
  Briefcase, Loader2, AlertCircle, Tag, ChevronDown, ChevronUp,
  User, History, ExternalLink, BellOff
} from "lucide-react";
import { 
  PIPELINE_STAGES, PRIORITY_LEVELS, JOB_TAGS, PAST_JOB_STATUSES,
  getStatusInfo, normalizeJobStatus, getServiceLabel, formatDate 
} from "@/lib/constants";
import { MetricInfo } from "./MetricInfo";

const PAST_STATUSES = PAST_JOB_STATUSES;

// Reusable job row
const JobRow = ({ enquiry, allAdmins, customers, onViewJob, onEditJob, onScheduleJob, onGenerateQuote, onCreateInvoice, onMarkComplete, onDeleteJob, onViewCustomer, onSuppressReview, isPast = false, selectable = false, canSelect = true, selected = false, onToggleSelect }) => {
  const statusInfo = getStatusInfo(enquiry.status, 'job');
  const priorityInfo = PRIORITY_LEVELS[enquiry.priority] || PRIORITY_LEVELS.normal;
  const assignedAdmin = allAdmins.find(a => a.email === enquiry.assigned_admin);
  const linkedCustomer = enquiry.customer_id ? customers?.find(c => c.id === enquiry.customer_id) : null;
  
  const status = normalizeJobStatus(enquiry.status);
  // Iter 69/70: align the "needs action" amber highlight with the dashboard
  // counter — only flag paid jobs created on/after 2025-12-01 that haven't
  // been asked or actively suppressed.
  const reviewIsActionable = (() => {
    if (status !== 'paid') return false;
    if (enquiry.review_status !== 'not_requested') return false;
    const ts = (enquiry.created_at || enquiry.date_completed || '').slice(0, 10);
    return ts && ts >= '2025-12-01';
  })();
  const needsAction = !isPast && (
    (status === 'accepted' && !enquiry.preferred_date) ||
    (status === 'completed' && !enquiry.invoice_id) ||
    reviewIsActionable ||
    enquiry.priority === 'urgent'
  );
  
  return (
    <TableRow 
      key={enquiry.id} 
      className={`cursor-pointer hover:bg-muted/50 ${needsAction ? 'bg-amber-50/50' : ''} ${selected ? 'bg-primary/5' : ''}`} 
      onClick={() => onViewJob(enquiry)}
      data-testid={`job-row-${enquiry.id}`}
    >
      {selectable && (
        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
          {canSelect && (
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect?.(enquiry.id)}
              data-testid={`job-select-${enquiry.id}`}
              aria-label={`Select ${enquiry.name || enquiry.id}`}
            />
          )}
        </TableCell>
      )}
      <TableCell>
        <div className="flex items-center gap-2">
          {needsAction && <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
          <div>
            <p className="font-medium">{enquiry.name}</p>
            {linkedCustomer ? (
              <button
                className="flex items-center gap-1 text-xs text-primary hover:underline"
                onClick={(e) => { e.stopPropagation(); onViewCustomer?.(linkedCustomer); }}
                data-testid={`customer-link-${enquiry.id}`}
              >
                <User className="h-3 w-3" />
                {linkedCustomer.name}
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">{enquiry.phone}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>{getServiceLabel(enquiry.service)}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge className={statusInfo.bgColor}>{statusInfo.label}</Badge>
          {enquiry.tags?.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {enquiry.tags.slice(0, 2).map(tag => (
                <span key={tag} className={`text-[10px] px-1.5 py-0 rounded-full ${JOB_TAGS[tag]?.bgColor || 'bg-gray-100'}`}>
                  {JOB_TAGS[tag]?.label || tag}
                </span>
              ))}
              {enquiry.tags.length > 2 && (
                <span className="text-[10px] text-muted-foreground">+{enquiry.tags.length - 2}</span>
              )}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={priorityInfo.bgColor}>{priorityInfo.label}</Badge>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">
          {enquiry.all_team_assigned 
            ? 'All Team'
            : enquiry.assigned_admins?.length > 0
              ? enquiry.assigned_admins.map(email => allAdmins.find(a => a.email === email)?.name?.split(' ')[0] || '?').join(', ')
              : assignedAdmin?.name || '-'
          }
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm">{enquiry.estimated_value ? `£${enquiry.estimated_value.toLocaleString()}` : '-'}</span>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {isPast
          ? formatDate(enquiry.date_completed) || formatDate(enquiry.preferred_date) || formatDate(enquiry.created_at)
          : formatDate(enquiry.preferred_date) || formatDate(enquiry.created_at)}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewJob(enquiry); }}>
              <Eye className="h-4 w-4 mr-2" /> View Details
            </DropdownMenuItem>
            {linkedCustomer && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewCustomer?.(linkedCustomer); }}>
                <User className="h-4 w-4 mr-2" /> View Customer
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditJob(enquiry); }}>
              <Edit3 className="h-4 w-4 mr-2" /> Edit Job
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewJob(enquiry); }}>
              <ArrowUpDown className="h-4 w-4 mr-2" /> Move Stage
            </DropdownMenuItem>
            {!isPast && (
              <>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onScheduleJob(enquiry); }}>
                  <CalendarIcon className="h-4 w-4 mr-2" /> Schedule
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onGenerateQuote(enquiry); }}>
                  <FileText className="h-4 w-4 mr-2" /> Generate Quote
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCreateInvoice(enquiry); }}>
                  <PoundSterling className="h-4 w-4 mr-2" /> Create Invoice
                </DropdownMenuItem>
                {enquiry.status !== 'completed' && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMarkComplete(enquiry.id); }}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Mark Complete
                  </DropdownMenuItem>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            {enquiry.review_status !== 'suppressed' && enquiry.review_status !== 'received' && (
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onSuppressReview?.([enquiry.id]); }}
                data-testid={`suppress-review-${enquiry.id}`}
              >
                <BellOff className="h-4 w-4 mr-2" /> Suppress review request
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              className="text-destructive focus:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDeleteJob(enquiry.id); }}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

export const JobsTab = ({
  enquiries = [],
  customers = [],
  allAdmins = [],
  isLoading = false,
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  onAddJob,
  onViewJob,
  onEditJob,
  onScheduleJob,
  onGenerateQuote,
  onCreateInvoice,
  onMarkComplete,
  onDeleteJob,
  onStatusChange,
  onViewCustomer,
  onRefresh
}) => {
  const [filterTag, setFilterTag] = useState("all");
  // Iter 60: Auto-expand so Past Jobs are visible by default — previously
  // collapsed behaviour made the Completed/Paid list look empty.
  const [showPastJobs, setShowPastJobs] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // Iter 69: Bulk-suppress review requests on Past Jobs.  Selection lives
  // here (not on each row) so a Select-All checkbox in the table header can
  // toggle every visible past job in one click.
  const [selectedPastIds, setSelectedPastIds] = useState(() => new Set());
  const [isSuppressing, setIsSuppressing] = useState(false);
  const togglePastSelection = (id) => {
    setSelectedPastIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Iter 60: Retroactive completion-date backfill. Syncs every completed /
  // invoiced / paid job's date_completed to the linked invoice's invoice_date.
  const handleSyncCompletionDates = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const API = window.location.origin;
      const res = await fetch(`${API}/api/admin/jobs/backfill-completion-dates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Lazy-load toast so we don't bloat the bundle
      const { toast } = await import('sonner');
      toast.success(`Synced ${data.updated} job completion date${data.updated === 1 ? '' : 's'} (skipped ${data.skipped})`);
      onRefresh?.();
    } catch (err) {
      console.error('Backfill failed:', err);
      const { toast } = await import('sonner');
      toast.error('Failed to sync completion dates');
    } finally {
      setIsSyncing(false);
    }
  };

  // Iter 69: Bulk-suppress review requests.  Accepts either an explicit
  // array of ids (used by the per-row dropdown action) or, when called
  // with no args, every currently selected past job.  Sets
  // review_status='suppressed' server-side so the dashboard "No Review
  // Request" counter immediately drops by N.
  const handleBulkSuppress = async (ids) => {
    const targetIds = Array.isArray(ids) && ids.length ? ids : Array.from(selectedPastIds);
    if (!targetIds.length) return;
    if (isSuppressing) return;
    if (!window.confirm(
      `Suppress review request for ${targetIds.length} job${targetIds.length === 1 ? '' : 's'}?\n\n` +
      `No messages will be sent to customers.  This simply removes the rows from the "No Review Request" counter.`
    )) return;
    setIsSuppressing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const API = window.location.origin;
      const res = await fetch(`${API}/api/admin/jobs/bulk-suppress-review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ job_ids: targetIds }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const { toast } = await import('sonner');
      toast.success(
        `Suppressed ${data.modified} review request${data.modified === 1 ? '' : 's'}` +
        (data.skipped ? ` (skipped ${data.skipped})` : '')
      );
      setSelectedPastIds(new Set());
      onRefresh?.();
    } catch (err) {
      console.error('Bulk suppress failed:', err);
      const { toast } = await import('sonner');
      toast.error('Failed to suppress review requests');
    } finally {
      setIsSuppressing(false);
    }
  };

  // Split active vs past jobs
  const activeEnquiries = enquiries.filter(e => !PAST_STATUSES.includes(normalizeJobStatus(e.status)));
  const pastEnquiries = enquiries.filter(e => PAST_STATUSES.includes(normalizeJobStatus(e.status)));

  // Filter active based on search, status, and tag
  const filteredActive = activeEnquiries.filter(e => {
    const matchesSearch = !searchTerm || 
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.phone?.includes(searchTerm) ||
      e.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const normalizedStatus = normalizeJobStatus(e.status);
    const matchesStatus = filterStatus === 'all' || normalizedStatus === filterStatus;
    const matchesTag = filterTag === 'all' || (e.tags && e.tags.includes(filterTag));
    return matchesSearch && matchesStatus && matchesTag;
  });

  // Filter past based on search only, then sort by completion date DESC
  // (newest completed first). Falls back to invoice date, created_at.
  const filteredPast = pastEnquiries
    .filter(e => {
      if (!searchTerm) return true;
      return e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.phone?.includes(searchTerm) ||
        e.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.date_completed || '').includes(searchTerm);
    })
    .sort((a, b) => {
      const da = (a.date_completed || a.preferred_date || a.created_at || '').slice(0, 10);
      const db = (b.date_completed || b.preferred_date || b.created_at || '').slice(0, 10);
      return db.localeCompare(da); // DESC
    });

  const sharedRowProps = { allAdmins, customers, onViewJob, onEditJob, onScheduleJob, onGenerateQuote, onCreateInvoice, onMarkComplete, onDeleteJob, onViewCustomer, onSuppressReview: handleBulkSuppress };

  // Iter 69: ids of past-jobs that are eligible for the bulk select-all
  // checkbox — exclude rows already actioned (received / requested /
  // suppressed) so we never re-flip those.
  const selectableIds = useMemo(() => filteredPast
    .filter(e => !['received', 'requested', 'suppressed'].includes(e.review_status))
    .map(e => e.id), [filteredPast]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedPastIds.has(id));
  const someSelected = selectableIds.some(id => selectedPastIds.has(id));
  const toggleSelectAll = () => {
    setSelectedPastIds(allSelected ? new Set() : new Set(selectableIds));
  };

  const TableHeaders = ({ withSelect = false } = {}) => (
    <TableHeader>
      <TableRow>
        {withSelect && (
          <TableHead className="w-10">
            <Checkbox
              checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
              onCheckedChange={toggleSelectAll}
              data-testid="past-jobs-select-all"
              aria-label="Select all past jobs"
            />
          </TableHead>
        )}
        <TableHead className="min-w-[150px]">Customer</TableHead>
        <TableHead className="min-w-[100px]">Service</TableHead>
        <TableHead className="min-w-[100px]">Stage</TableHead>
        <TableHead className="min-w-[80px]">Priority</TableHead>
        <TableHead className="min-w-[100px]">Assigned</TableHead>
        <TableHead className="min-w-[80px]">Value</TableHead>
        <TableHead className="min-w-[100px]">Date</TableHead>
        <TableHead className="text-right min-w-[80px]">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="space-y-4">
      {/* Active Pipeline */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Jobs Pipeline
                <Badge variant="secondary" className="ml-1">{filteredActive.length}</Badge>
                <MetricInfo metricKey="jobs_active_pipeline" />
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSyncCompletionDates}
                  size="sm"
                  variant="outline"
                  disabled={isSyncing}
                  data-testid="sync-completion-dates-btn"
                  title="Sync every completed/invoiced/paid job's completion date to the linked invoice date"
                >
                  {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />}
                  <span className="ml-1 hidden sm:inline">{isSyncing ? 'Syncing…' : 'Sync Dates'}</span>
                </Button>
                <Button onClick={onAddJob} size="sm" className="bg-accent gap-1" data-testid="add-job-btn">
                  <Plus className="h-4 w-4" />New Job
                </Button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search jobs..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-9 h-9"
                  data-testid="job-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-40 h-9" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {PIPELINE_STAGES.map(stage => (
                    <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="w-full sm:w-44 h-9" data-testid="tag-filter">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {Object.entries(JOB_TAGS).map(([tagId, tagInfo]) => (
                    <SelectItem key={tagId} value={tagId}>
                      {tagInfo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filterTag !== 'all' && (
              <div className="flex items-center gap-2">
                <Badge className={JOB_TAGS[filterTag]?.bgColor || 'bg-gray-100'}>
                  {JOB_TAGS[filterTag]?.label || filterTag}
                </Badge>
                <button 
                  onClick={() => setFilterTag('all')} 
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="clear-tag-filter"
                >
                  Clear filter
                </button>
                <span className="text-xs text-muted-foreground">
                  ({filteredActive.length} job{filteredActive.length !== 1 ? 's' : ''})
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredActive.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No active jobs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeaders />
                <TableBody>
                  {filteredActive.map((enquiry) => (
                    <JobRow key={enquiry.id} enquiry={enquiry} {...sharedRowProps} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Jobs */}
      {pastEnquiries.length > 0 && (
        <Card>
          <CardHeader className="p-3 sm:p-6 cursor-pointer" onClick={() => setShowPastJobs(!showPastJobs)} data-testid="past-jobs-toggle">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2 text-muted-foreground">
                <History className="h-5 w-5" />
                Past Jobs
                <Badge variant="outline" className="ml-1">{filteredPast.length}</Badge>
                <MetricInfo metricKey="jobs_past" />
              </CardTitle>
              {showPastJobs ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
            </div>
          </CardHeader>
          {showPastJobs && (
            <CardContent className="p-0 sm:p-6 sm:pt-0">
              {selectedPastIds.size > 0 && (
                <div
                  className="flex items-center justify-between gap-3 px-3 py-2 sm:px-0 sm:pb-3 mb-2 border-b sticky top-0 bg-background z-10"
                  data-testid="bulk-suppress-action-bar"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-sm font-medium" data-testid="bulk-suppress-count">
                    {selectedPastIds.size} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedPastIds(new Set())}
                      data-testid="bulk-suppress-clear"
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleBulkSuppress()}
                      disabled={isSuppressing}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="bulk-suppress-btn"
                    >
                      {isSuppressing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <BellOff className="h-4 w-4 mr-1.5" />}
                      Suppress review request
                    </Button>
                  </div>
                </div>
              )}
              {filteredPast.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No past jobs match your search</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeaders withSelect />
                    <TableBody>
                      {filteredPast.map((enquiry) => (
                        <JobRow
                          key={enquiry.id}
                          enquiry={enquiry}
                          {...sharedRowProps}
                          isPast
                          selectable
                          canSelect={!['received', 'requested', 'suppressed'].includes(enquiry.review_status)}
                          selected={selectedPastIds.has(enquiry.id)}
                          onToggleSelect={togglePastSelection}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default JobsTab;
